import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { loadTranslations, translate } from '../i18n';
import { formatCurrency, formatDate } from '../i18n/utils/helpers';

export const useTranslation = () => {
  const { language } = useLanguage();
  const [translations, setTranslations] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load translations when language changes
  useEffect(() => {
    const loadLanguageTranslations = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const translationData = await loadTranslations(language);
        setTranslations(translationData);
      } catch (err) {
        console.error('Failed to load translations:', err);
        setError(err);
        // Set empty object as fallback
        setTranslations({});
      } finally {
        setIsLoading(false);
      }
    };

    loadLanguageTranslations();
  }, [language]);

  // Translation function
  const t = useCallback((key, options = {}) => {
    return translate(key, options, translations, language);
  }, [translations, language]);

  // Specialized formatting functions
  const formatters = {
    currency: useCallback((amount, currency) => {
      return formatCurrency(amount, currency, language);
    }, [language]),

    date: useCallback((date) => {
      return formatDate(date, language);
    }, [language]),

    // Helper for translating with count (pluralization)
    plural: useCallback((key, count, variables = {}) => {
      return t(key, { count, variables });
    }, [t])
  };

  // Get translations for a specific namespace (e.g., 'checkout', 'modals')
  const getNamespace = useCallback((namespace) => {
    return translations[namespace] || {};
  }, [translations]);

  // Check if a translation key exists
  const hasTranslation = useCallback((key) => {
    return translate(key, {}, translations, language) !== key;
  }, [translations, language]);

  // Get current language info
  const languageInfo = {
    code: language,
    isRTL: false, // None of our supported languages are RTL currently
    locale: language === 'pt_br' ? 'pt-BR' : 'en-US'
  };

  return {
    t,
    language,
    languageInfo,
    translations,
    isLoading,
    error,
    formatters,
    getNamespace,
    hasTranslation
  };
}; 